import { ActorRefFrom, assign, createMachine, not, sendTo } from 'xstate'

import Survey, { SurveyAudienceKey, SurveyName, SurveyQuestionKey, SurveyStartKey } from '@/services/Survey'
import { Audience, Config, configs, LTD, Persona, questions } from '@/state/AppConfig'

import { AppChooseEvent, AppEvent } from './AppEvent'
import { CreateRunnerStateMachine, RunnerStartEvent, RunnerStateMachine } from './RunnerStateMachine'

/// #if _DEBUG
import { AppDevRunner } from './AppEvent'
/// #endif

export type AnswerGroup = { [key: string]: string }

export type Answers = {
  before: AnswerGroup
  after: AnswerGroup
}

export enum CompletionStatus {
  NOT_STARTED = 'not_started',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

export type Scenario = {
  current: number | null
  status: CompletionStatus[]
}

export type AppContext = {
  initialized: boolean
  locale?: string
  userId: string
  loaded: boolean
  backUrl?: string

  persona: Persona | null
  relationship?: Audience
  interestedBy?: LTD

  answers: Answers
  scenario: Scenario

  summary: {
    scenario?: number
  }

  runner: ActorRefFrom<RunnerStateMachine>
}

export type AppState = {
  value: string
  context: AppContext
}

const initialState = import.meta.env.VITE_INITIAL_STATE ? import.meta.env.VITE_INITIAL_STATE : 'idle'
// const initialPersona = import.meta.env.VITE_INITIAL_PERSONA ? import.meta.env.VITE_INITIAL_PERSONA as Persona : null;
const initialScenario = import.meta.env.VITE_INITIAL_SCENARIO ? parseInt(import.meta.env.VITE_INITIAL_SCENARIO) : null

const isLockedPersona = import.meta.env.VITE_LOCKED_PERSONA ? import.meta.env.VITE_LOCKED_PERSONA.split(',') : []

function assignQuestion(answerGroup: AnswerGroup, question: string, answer: string) {
  if (question && answer) {
    answerGroup[question] = answer.toUpperCase()
  }

  return answerGroup
}

export function CreateAppStateMachine() {
  return createMachine(
    {
      id: 'app',

      types: {
        context: {} as AppContext,
        events: {} as AppEvent,
      },

      context: ({ spawn }): AppContext => ({
        initialized: false,
        locale: null,
        userId: 'anon-user',
        loaded: false,

        persona: null,
        relationship: null,
        interestedBy: null,

        answers: {
          before: {},
          after: {},
        },
        scenario: {
          current: initialScenario,
          status: [],
        },

        summary: {
          scenario: null,
        },

        runner: spawn(CreateRunnerStateMachine(), { id: 'runner' }),
      }),

      type: 'compound',

      initial: initialState,

      on: {
        /// #if _DEBUG
        DEV_RUNNER: {
          target: '#running',
          actions: assign(({ event }) => {
            const scenario: Scenario = {
              current: (event as AppDevRunner).scenario,
              status: [],
            }
            return {
              persona: (event as AppDevRunner).persona,
              scenario,
            }
          }),
        },
        /// #endif

        INITIALIZE: [
          {
            guard: ({ context }) => !context.initialized,
            actions: assign(({ event }) => {
              // console.log('[AppStateMachine] INITIALIZE', event)
              const personaConfig: Config | undefined = configs.find((list) => list.persona === event.persona)

              const scenario: Scenario = {
                current: null,
                status: personaConfig?.scenarios.map(() => CompletionStatus.NOT_STARTED) || [],
              }
              return {
                initialized: true,
                locale: event.locale,
                userId: event.userId,
                backUrl: event.backUrl,
                persona: event.persona,
                relationship: event.relationship,
                interestedBy: event.interestedBy,
                scenario,
              }
            }),
          },
        ],
      },
      always: [
        {
          guard: 'isLockedPersona',
          target: '#idle',
        },
      ],

      states: {
        idle: {
          id: 'idle',
          // entry: () => console.log('[AppStateMachine] idle'),
          on: {
            CHOOSE: {
              actions: [
                assign(({ context, event }) => {
                  const persona = (event as AppChooseEvent).persona ?? context.persona
                  const personaConfig: Config | undefined = configs.find((list) => list.persona === persona)
                  const scenario: Scenario = {
                    current: null,
                    status: personaConfig?.scenarios.map(() => CompletionStatus.NOT_STARTED) ?? [],
                  }
                  return {
                    persona,
                    relationship: (event as AppChooseEvent).relationship,
                    interestedBy: (event as AppChooseEvent).interestedBy,
                    scenario,
                  }
                }),
              ],
              target: '#loading',
            },
          },
          always: [
            {
              guard: 'hasPersona',
              target: '#loading',
            },
          ],
        },

        loading: {
          id: 'loading',
          // entry: () => console.log('[AppStateMachine] loading'),
          initial: 'catch',
          always: [
            {
              guard: 'isLoadingSkip',
              target: '#intro',
            },
          ],
          states: {
            catch: {
              on: {
                NEXT: {
                  actions: assign({ loaded: true }),
                  target: 'proceed',
                },
              },
            },
            proceed: {
              on: {
                LOADED: {
                  actions: assign({ loaded: true }),
                  target: '#intro',
                },
              },
            },
          },
        },

        intro: {
          id: 'intro',
          // entry: () => console.log('[AppStateMachine] intro'),
          always: [
            {
              guard: 'isIntroSkip',
              target: 'scenario',
            },
          ],
          on: {
            NEXT: {
              target: 'scenario',
            },
            SKIP: {
              target: 'scenario',
            },
          },
        },

        preliminary: {
          id: 'preliminary',
          // entry: () => console.log('[AppStateMachine] preliminary'),
          on: {
            CHOOSE: [
              {
                actions: [
                  assign(({ event }) => {
                    return {
                      relationship: (event as AppChooseEvent).answer as Audience,
                    }
                  }),
                ],
              },
            ],
          },
          always: [
            {
              guard: 'hasRelationship',
              target: '#questions.before',
            },
          ],
        },

        scenario: {
          // entry: () => console.log('[AppStateMachine] scenario'),
          id: 'scenario',
          always: {
            guard: not('hasScenariosToPlay'),
            target: '#outro',
          },
          initial: 'picker',
          states: {
            picker: {
              on: {
                CHOOSE: {
                  target: '#preliminary',
                  actions: assign(({ event, context }) => {
                    const newStatus = [...context.scenario.status]
                    newStatus[event.scenario] = CompletionStatus.COMPLETED
                    return {
                      scenario: {
                        ...context.scenario,
                        status: newStatus,
                        current: event.scenario,
                      },
                    }
                  }),
                },
                SKIP: [
                  {
                    guard: ({ context }) => {
                      return context.scenario.status.some((status) => status === CompletionStatus.COMPLETED)
                    },
                    target: '#outro',
                    actions: assign(({ context }) => {
                      const newStatus = [...context.scenario.status].map((status) => {
                        return status === CompletionStatus.NOT_STARTED ? CompletionStatus.SKIPPED : status
                      })
                      return {
                        scenario: {
                          ...context.scenario,
                          status: newStatus,
                        },
                      }
                    }),
                  },
                ],
              },
            },
          },
        },

        questions: {
          id: 'questions',
          initial: 'before',
          states: {
            before: {
              always: [{ target: '#running', guard: 'isQuestionsBeforeFinished' }],
              entry: [
                // () => console.log('[AppStateMachine] questions:before'),
                {
                  type: 'survey.audience',
                  params: ({ context }) => ({ audience: context.relationship, ltd: context.interestedBy }),
                },
                {
                  type: 'survey.start',
                  params: ({ context }) => ({
                    surveyName: `${context.persona.toUpperCase()}_${context.scenario.current}_PRE`,
                    startKey: `${context.persona.toUpperCase()}_${context.scenario.current}_PRE_USER_ID`,
                    audienceKey: `${context.persona.toUpperCase()}_${context.scenario.current}_PRE_AUDIENCE`,
                  }),
                },
              ],
              initial: 'choose',
              states: {
                choose: {
                  // entry: () => console.log('[AppStateMachine] questions:before:choose'),
                  on: {
                    CHOOSE: {
                      actions: [
                        assign(({ event, context }) => {
                          const answers = (context as AppContext).answers
                          answers.before = assignQuestion(
                            answers.before,
                            (event as AppChooseEvent).question,
                            (event as AppChooseEvent).answer
                          )
                          // console.log('[AppStateMachine] questions:before:choose', { event })
                          return { answers }
                        }),
                        {
                          type: 'survey.update',
                          params: ({ event }) => {
                            const questionKey = (event as AppChooseEvent).questionKey
                            const answer = (event as AppChooseEvent).answer.toUpperCase()
                            return { questionKey, answer }
                          },
                        },
                      ],
                      target: 'validate',
                    },
                  },
                },
                validate: {
                  // entry: () => console.log('[AppStateMachine] questions:before:validate'),
                  always: [{ target: '#running', guard: 'isQuestionsBeforeFinished' }, { target: 'choose' }],
                },
              },
            },
            after: {
              entry: [
                // () => console.log('[AppStateMachine] questions:after'),
                {
                  type: 'survey.start',
                  params: ({ context }) => ({
                    surveyName: `${context.persona.toUpperCase()}_${context.scenario.current}_POST`,
                    startKey: `${context.persona.toUpperCase()}_${context.scenario.current}_POST_USER_ID`,
                    audienceKey: `${context.persona.toUpperCase()}_${context.scenario.current}_POST_AUDIENCE`,
                  }),
                },
              ],
              initial: 'choose',
              always: [{ target: '#scenario', guard: 'isQuestionsAfterFinished' }],
              states: {
                choose: {
                  // entry: () => console.log('[AppStateMachine] questions:after:choose'),
                  on: {
                    CHOOSE: {
                      actions: [
                        assign(({ event, context }) => {
                          const answers = (context as AppContext).answers
                          answers.after = assignQuestion(
                            answers.after,
                            (event as AppChooseEvent).question,
                            (event as AppChooseEvent).answer
                          )
                          // console.log('[AppStateMachine] questions:after:choose', { answers })
                          return { answers }
                        }),
                        {
                          type: 'survey.update',
                          params: ({ event }) => {
                            const questionKey = (event as AppChooseEvent).questionKey
                            const answer = (event as AppChooseEvent).answer.toUpperCase()
                            return { questionKey, answer }
                          },
                        },
                      ],
                      target: 'validate',
                    },
                  },
                },
                validate: {
                  // entry: () => console.log('[AppStateMachine] questions:after:validate'),
                  always: [{ target: '#scenario', guard: 'isQuestionsAfterFinished' }, { target: 'choose' }],
                },
              },
            },
          },
        },

        running: {
          id: 'running',
          on: {
            EXIT_RUNNER: {
              target: '#ending',
            },
          },
          entry: [
            // () => console.log('[AppStateMachine] running'),
            sendTo(
              'runner',
              (context) =>
                ({
                  type: 'START',
                  scenario: context.context.scenario.current,
                  persona: context.context.persona,
                }) as RunnerStartEvent
            ),
          ],
        },

        ending: {
          // entry: () => console.log('[AppStateMachine] ending'),
          id: 'ending',
          on: {
            NEXT: [
              {
                guard: not('hasScenariosToPlay'),
                target: '#outro',
              },
              {
                target: '#summary',
              },
            ],
          },
        },

        outro: {
          // entry: () => console.log('[AppStateMachine] outro'),
          id: 'outro',
          always: [
            {
              guard: 'isOutroSkip',
              target: '#summary',
            },
          ],
          on: {
            NEXT: [
              {
                target: '#close',
                guard: ({ context }) => {
                  return context.scenario.status.some((status) => status === CompletionStatus.SKIPPED)
                },
              },
              {
                target: '#summary',
              },
            ],
            SKIP: [
              {
                target: '#close',
                guard: ({ context }) => {
                  return context.scenario.status.some((status) => status === CompletionStatus.SKIPPED)
                },
              },
              {
                target: '#summary',
              },
            ],
          },
        },

        summary: {
          id: 'summary',
          entry: [
            // () => console.log('[AppStateMachine] summary'),
            assign(({ context }) => ({ summary: { scenario: (context as AppContext).scenario.current } })),
          ],
          initial: 'intro',
          states: {
            intro: {
              on: {
                NEXT: [
                  {
                    target: 'content',
                  },
                ],
              },
            },
            content: {
              on: {
                NEXT: [
                  {
                    actions: assign(({ context, event }) => {
                      const persona = event.persona ?? context.persona
                      const personaConfig: Config | undefined = configs.find((list) => list.persona === persona)
                      const scenario: Scenario = {
                        current: null,
                        status: personaConfig?.scenarios.map((s) => CompletionStatus.NOT_STARTED) ?? [],
                      }
                      const summary: AppContext['summary'] = {
                        scenario: null,
                      }
                      return {
                        persona,
                        scenario,
                        answers: {
                          before: {},
                          after: {},
                        },
                        summary,
                      }
                    }),
                    guard: not('hasScenariosToPlay'),
                    target: '#loading.proceed',
                  },
                  {
                    target: '#questions.after',
                  },
                ],

                CHOOSE: {
                  actions: assign(({ event }) => ({ summary: { scenario: (event as AppChooseEvent).scenario } })),
                },
              },
            },
          },
        },

        close: {
          id: 'close',
        },
      },
    },
    {
      actions: {
        'survey.audience': (_, eventData: object) => {
          const { audience, ltd } = eventData as { audience: Audience; ltd: LTD }
          // console.log('[AppStateMachine] Survey.audience', _, audience, ltd)
          // Survey failures are already reported to Sentry (reportError) inside the service.
          // These are fire-and-forget actions, so swallow the rejection here to avoid an
          // unhandled promise rejection.
          Survey.audience(audience, ltd, _.context.userId).catch(() => {})
        },

        'survey.start': (_, eventData: object) => {
          // console.log('[AppStateMachine] Survey.start', _, eventData)
          const { startKey, surveyName, audienceKey } = eventData as {
            startKey: keyof typeof SurveyStartKey
            surveyName: keyof typeof SurveyName
            audienceKey: keyof typeof SurveyAudienceKey
          }
          Survey.start(startKey, audienceKey, surveyName, _.context.userId, _.context.relationship).catch(() => {})
        },

        'survey.update': (_, eventData: object) => {
          // console.log('[AppStateMachine] Survey.update', eventData)
          const { questionKey, answer } = eventData as { questionKey?: SurveyQuestionKey; answer: string }
          if (questionKey) {
            Survey.update(questionKey, answer).catch(() => {})
          }
        },
      },
      guards: {
        isQuestionsBeforeFinished: ({ context }) => {
          const answers = (context as AppContext).answers
          const questionsAsked = context.relationship === 'CHILD' ? questions.before_long : questions.before
          return Object.keys(answers.before).length === questionsAsked.length
        },
        isQuestionsAfterFinished: ({ context }) => {
          const answers = (context as AppContext).answers
          const questionsAsked = context.relationship === 'CHILD' ? questions.after_long : questions.after
          return Object.keys(answers.after).length === questionsAsked.length
        },

        // SKIPPERS
        isIntroSkip: () => import.meta.env.VITE_SKIP_INTRO === 'true',
        isOutroSkip: () => import.meta.env.VITE_SKIP_OUTRO === 'true',
        isLoadingSkip: () => import.meta.env.VITE_SKIP_LOADING === 'true',

        // LOCKED PERSONA
        isLockedPersona: ({ context }) => isLockedPersona.includes((context as AppContext).persona),

        // IDLE STATE SKIP
        hasScenariosToPlay: ({ context }) => {
          return context.scenario.status.some((status) => status === CompletionStatus.NOT_STARTED)
        },
        hasPersona: ({ context }) => {
          const persona = (context as AppContext).persona
          // console.log('[AppStateMachine] hasPersona', { persona })
          return persona !== null
        },
        hasRelationship: ({ context }) => {
          const relationship = (context as AppContext).relationship
          // console.log('[AppStateMachine] hasRelationship', { relationship })
          return relationship !== null && relationship !== 'UNSPECIFIED'
        },
      },
    }
  )
}

export type AppStateMachine = ReturnType<typeof CreateAppStateMachine>
